/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { ChevronDown, Divider, Icon, Text, View } from '@az/starc-ui'
import { Padding } from '@az/starc-ui/dist/components/view/View.types'
import { Responsive } from '@az/starc-ui/dist/types/global'
import { FC, PropsWithChildren, ReactNode, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { HeightTransition } from '../HeightTransition'

interface AnimatedIconProps {
  open?: boolean
}

export const AnimatedIcon = styled(Icon)<AnimatedIconProps>(({ open }) => ({
  transform: open ? 'rotate(0.5turn)' : undefined,
  transition: 'all var(--st-duration-slow) var(--st-easing-standard);',
}))

interface CustomAnimatedAccordionProps extends PropsWithChildren {
  header?: ReactNode
  headerFn?: (isOpen?: boolean) => ReactNode
  open?: boolean
  defaultOpen?: boolean
  className?: string
  showToggleNextToHeader?: boolean
  disabled?: boolean
  hideDivider?: boolean
  headerPadding?: Responsive<Padding>
  headerWidth?: Responsive<string>
  justifyHeader?: Responsive<
    'center' | 'start' | 'end' | 'space-between' | 'space-around' | 'space-evenly'
  >
  alignHeader?: Responsive<'center' | 'start' | 'end' | 'stretch' | 'baseline'>
}

export const CustomAnimatedAccordion: FC<CustomAnimatedAccordionProps> = ({
  open: openProp,
  header,
  headerFn,
  children,
  defaultOpen = false,
  className,
  showToggleNextToHeader,
  disabled,
  justifyHeader,
  alignHeader,
  hideDivider,
  headerPadding,
  headerWidth,
}) => {
  const [showContent, setShowContent] = useState(defaultOpen)

  useEffect(() => {
    if (openProp != undefined) {
      setShowContent(openProp)
    }
  }, [openProp])

  return (
    <View
      direction="column"
      borderRadius="small"
      backgroundColor="secondary"
      className={`qa-Accordion${className ? '-' + className : ''}:${
        !disabled && showContent ? 'Expanded' : 'Collapsed'
      }`}
    >
      <View
        justify={justifyHeader ? justifyHeader : 'center'}
        align="center"
        direction="row"
        attributes={{
          style: {
            cursor: disabled ? 'default' : 'pointer',
          },
          onClick: disabled ? undefined : () => setShowContent(value => !value),
        }}
      >
        <View
          height="50px"
          width={headerWidth != undefined ? headerWidth : '100%'}
          align={alignHeader ? alignHeader : 'center'}
          padding={headerPadding}
          direction="row"
        >
          <View.Item grow={!showToggleNextToHeader}>
            {typeof header == 'string' ? (
              <Text variant="display-5">{header}</Text>
            ) : (
              <>{header || headerFn?.(!disabled && showContent)}</>
            )}
          </View.Item>
          <View padding={[0, 4]}>
            <AnimatedIcon
              svg={ChevronDown}
              open={!disabled && showContent}
              color={!disabled ? 'primary' : 'disabled'}
            />
          </View>
        </View>
      </View>
      {!hideDivider ? <Divider /> : <></>}
      <HeightTransition show={!disabled && showContent}>
        <View direction="column">{children}</View>
      </HeightTransition>
    </View>
  )
}
